import { rgb, StandardFonts } from 'pdf-lib'
import { resolvePrintedOfferInfo } from '@blocktickets/shared-utils'

const PAGE_WIDTH = 612
const PAGE_HEIGHT = 792
const TICKET_X = 43.2
const TICKET_W = 525.6
const TICKET_H = 316.8
const TICKET_GAP = 20
// Top ticket sits just under the page header; bottom ticket fills the lower half.
const TOP_TICKET_Y = PAGE_HEIGHT - 48 - TICKET_H // 427.2
const BOTTOM_TICKET_Y = TOP_TICKET_Y - TICKET_GAP - TICKET_H // 90.4
const HEADER_H = 64.8
const STRIPE_W = 13
const CORNER_R = 14
const DEFAULT_PRIMARY = '#1A365D'
const TICKETS_PER_PAGE = 2
const NM_STATE_ATHLETICS_ORG_UUID = 'dbeea528-852e-43d0-b607-c8201d7a5f1c'

const CATEGORY_THEMES = {
  sports: {
    badgeLabel: 'SPORTING EVENT',
    badgeColor: '#E53E3E',
    bodyTint: '#EBF8FF',
  },
  theater: {
    badgeLabel: 'THEATER EVENT',
    badgeColor: '#D69E2E',
    bodyTint: '#FFFFF0',
  },
  concert: {
    badgeLabel: 'CONCERT EVENT',
    badgeColor: '#ED64A6',
    bodyTint: '#FAF5FF',
  },
  family: {
    badgeLabel: 'FAMILY EVENT',
    badgeColor: '#38A169',
    bodyTint: '#F0FFF4',
  },
  access: {
    badgeLabel: 'ACCESS PASS',
    badgeColor: '#9757D7',
    bodyTint: '#F8F0FF',
  },
  default: {
    badgeLabel: 'EVENT TICKET',
    badgeColor: '#3182CE',
    bodyTint: '#F7FAFC',
  },
}

function hexToRgb(hex, fallback = DEFAULT_PRIMARY) {
  const raw = String(hex || fallback)
    .trim()
    .replace('#', '')
  const normalized =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return hexToRgb(fallback)
  }
  const int = parseInt(normalized, 16)
  return rgb(
    ((int >> 16) & 255) / 255,
    ((int >> 8) & 255) / 255,
    (int & 255) / 255
  )
}

function lightenHex(hex, amount = 0.92) {
  const color = hexToRgb(hex)
  return rgb(
    color.red + (1 - color.red) * amount,
    color.green + (1 - color.green) * amount,
    color.blue + (1 - color.blue) * amount
  )
}

// Contrast against white and black is equal at this relative luminance.
const CONTRAST_PIVOT = 0.179

function relativeLuminance(color) {
  const channel = (value) =>
    value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4)
  return (
    0.2126 * channel(color.red) +
    0.7152 * channel(color.green) +
    0.0722 * channel(color.blue)
  )
}

function isLightColor(color) {
  return relativeLuminance(color) > CONTRAST_PIVOT
}

/**
 * The brand color also acts as a label color on the pale ticket body, where
 * light brands wash out. Step it down until it reads against that background.
 * The ceiling keeps 7-8pt labels at WCAG AA (4.5:1) on the lightest body tint.
 */
function accentOn(primary, maxLuminance = 0.17) {
  let accent = primary
  for (let i = 0; i < 24 && relativeLuminance(accent) > maxLuminance; i += 1) {
    accent = rgb(accent.red * 0.88, accent.green * 0.88, accent.blue * 0.88)
  }
  return accent
}

function mediaUrl(media) {
  if (!media) return null
  if (typeof media === 'string') return media
  return (
    media.url ||
    media?.formats?.large?.url ||
    media?.formats?.medium?.url ||
    media?.formats?.small?.url ||
    media?.formats?.thumbnail?.url ||
    null
  )
}

function resolveAbsoluteUrl(url) {
  if (!url) return null
  const value = String(url).trim()
  if (!value) return null
  if (/^https?:\/\//i.test(value)) return value
  if (value.startsWith('//')) return `https:${value}`
  const base = String(process.env.REACT_APP_API || '')
    .trim()
    .replace(/\/api\/?$/, '')
    .replace(/\/+$/, '')
  if (!base) return value
  return `${base}${value.startsWith('/') ? '' : '/'}${value}`
}

export function resolveTicketCategoryKey(categoryName) {
  const name = String(categoryName || '')
    .trim()
    .toLowerCase()
  if (!name) return 'default'
  if (name.includes('sport')) return 'sports'
  if (name.includes('concert') || name.includes('music')) return 'concert'
  if (
    name.includes('theater') ||
    name.includes('theatre') ||
    name.includes('arts') ||
    name.includes('comedy')
  ) {
    return 'theater'
  }
  if (name.includes('family')) return 'family'
  if (name.includes('access')) return 'access'
  return 'default'
}

export function resolveTicketPrimaryColor(organization, event) {
  const branding =
    (event?.branding?.enabled !== false && event?.branding) ||
    (organization?.branding?.enabled !== false && organization?.branding) ||
    null
  return (
    event?.primaryColor ||
    branding?.primaryColor ||
    organization?.primaryColor ||
    organization?.brandColor ||
    DEFAULT_PRIMARY
  )
}

export function resolveTicketLogoUrl(organization, event) {
  const branding =
    (event?.branding?.enabled !== false && event?.branding) ||
    (organization?.branding?.enabled !== false && organization?.branding) ||
    null
  return resolveAbsoluteUrl(
    mediaUrl(branding?.logo) ||
      mediaUrl(branding?.darkLogo) ||
      mediaUrl(organization?.email_logo) ||
      mediaUrl(organization?.image) ||
      mediaUrl(organization?.logo) ||
      organization?.logoUrl ||
      null
  )
}

function resolveCategoryName(event, organization) {
  return (
    event?.category?.name ||
    event?.categoryName ||
    organization?.category?.name ||
    ''
  )
}

// Glyphs the standard PDF fonts can encode above Latin-1 (WinAnsi specials).
const WIN_ANSI_EXTRAS = new Set([
  0x20ac, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030,
  0x0160, 0x2039, 0x0152, 0x017d, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022,
  0x2013, 0x2014, 0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0x017e, 0x0178,
])

/**
 * Standard fonts throw on anything WinAnsi can't encode, so drop unsupported
 * glyphs and flatten newlines/tabs into single spaces for the one-line fields.
 */
function sanitizeText(text) {
  let output = ''
  for (const char of String(text ?? '')) {
    const code = char.codePointAt(0)
    if (
      (code >= 0x20 && code <= 0x7e) ||
      (code >= 0xa0 && code <= 0xff) ||
      WIN_ANSI_EXTRAS.has(code)
    ) {
      output += char
    } else if (/\s/.test(char)) {
      output += ' '
    }
  }
  // Collapse after filtering so dropped glyphs don't leave gaps behind.
  return output.replace(/\s+/g, ' ').trim()
}

function richTextToPlain(value) {
  return String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/[*_`>]/g, '')
}

function resolvePackageName(packageName, ticket, event) {
  const related = ticket?.package
  const pkg = Array.isArray(related) ? related[0] : related
  const name = String(packageName || pkg?.name || '').trim()
  // Package purchases title the ticket with the package itself; don't repeat it.
  return name === String(event?.name || '').trim() ? '' : name
}

function isNmStateAthleticsTicket(ticket, event, organization) {
  const organizationUuid =
    organization?.uuid ||
    organization?.organizationUUID ||
    event?.organizationUUID ||
    event?.organization?.uuid ||
    ticket?.organizationUUID ||
    ticket?.organization?.uuid
  return (
    String(organizationUuid || '').toLowerCase() === NM_STATE_ATHLETICS_ORG_UUID
  )
}

function resolveSubtitle(event) {
  if (event?.summary) return richTextToPlain(event.summary).trim()
  const attractions = event?.attractions
  if (Array.isArray(attractions) && attractions.length > 0) {
    return attractions
      .map((a) => a?.name)
      .filter(Boolean)
      .join('  •  ')
  }
  if (event?.subCategory?.name || event?.sub_category?.name) {
    return event.subCategory?.name || event.sub_category?.name
  }
  return ''
}

function fitText(text, font, size, maxWidth) {
  const value = sanitizeText(text)
  if (!value) return ''
  if (font.widthOfTextAtSize(value, size) <= maxWidth) return value
  const ellipsis = '…'
  let low = 0
  let high = value.length
  while (low < high) {
    const mid = Math.ceil((low + high) / 2)
    const candidate = `${value.slice(0, mid)}${ellipsis}`
    if (font.widthOfTextAtSize(candidate, size) <= maxWidth) low = mid
    else high = mid - 1
  }
  return low > 0 ? `${value.slice(0, low)}${ellipsis}` : ellipsis
}

function drawRoundedRect(
  page,
  { x, y, width, height, radius, color, borderColor, borderWidth = 1 }
) {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2))

  if (color) {
    if (r === 0) {
      page.drawRectangle({ x, y, width, height, color })
    } else {
      // Center + side rectangles
      page.drawRectangle({
        x: x + r,
        y,
        width: width - 2 * r,
        height,
        color,
      })
      page.drawRectangle({
        x,
        y: y + r,
        width,
        height: height - 2 * r,
        color,
      })
      // Corner circles
      page.drawCircle({ x: x + r, y: y + r, size: r, color })
      page.drawCircle({ x: x + width - r, y: y + r, size: r, color })
      page.drawCircle({ x: x + r, y: y + height - r, size: r, color })
      page.drawCircle({ x: x + width - r, y: y + height - r, size: r, color })
    }
  }

  if (borderColor) {
    // Approximate rounded border with a clean rectangular stroke.
    page.drawRectangle({
      x,
      y,
      width,
      height,
      borderColor,
      borderWidth,
    })
  }
}

async function embedLogo(pdfDoc, logoUrl, cache) {
  if (!logoUrl) return null
  if (cache[logoUrl]) return cache[logoUrl]
  try {
    const response = await fetch(logoUrl)
    if (!response.ok) return null
    const bytes = await response.arrayBuffer()
    const contentType = String(
      response.headers.get('content-type') || ''
    ).toLowerCase()
    let image
    if (contentType.includes('png') || logoUrl.toLowerCase().includes('.png')) {
      image = await pdfDoc.embedPng(bytes)
    } else if (
      contentType.includes('jpeg') ||
      contentType.includes('jpg') ||
      /\.jpe?g(\?|$)/i.test(logoUrl)
    ) {
      image = await pdfDoc.embedJpg(bytes)
    } else {
      try {
        image = await pdfDoc.embedPng(bytes)
      } catch {
        image = await pdfDoc.embedJpg(bytes)
      }
    }
    cache[logoUrl] = image
    return image
  } catch (err) {
    console.warn('Unable to embed ticket logo:', err)
    return null
  }
}

export async function createTicketPdfFonts(pdfDoc) {
  return {
    regular: await pdfDoc.embedFont(StandardFonts.Helvetica),
    bold: await pdfDoc.embedFont(StandardFonts.HelveticaBold),
  }
}

/**
 * Draw one branded category ticket onto an existing LETTER page at `ticketY`.
 */
async function drawBrandedTicket(
  page,
  pdfDoc,
  {
    ticket,
    event,
    organization,
    purchasedByOrganizationName,
    fonts,
    qrImage,
    ticketHolder,
    packageName,
    dateLabel,
    timeLabel,
    logoCache = {},
    ticketY = TOP_TICKET_Y,
    showPageChrome = false,
  }
) {
  const primaryHex = resolveTicketPrimaryColor(organization, event)
  const primary = hexToRgb(primaryHex)
  const categoryKey = resolveTicketCategoryKey(
    resolveCategoryName(event, organization)
  )
  const theme = CATEGORY_THEMES[categoryKey] || CATEGORY_THEMES.default
  const badgeColor = hexToRgb(theme.badgeColor)
  const bodyBg = theme.bodyTint
    ? hexToRgb(theme.bodyTint)
    : lightenHex(primaryHex, 0.94)
  const headerOnLight = isLightColor(primary)
  const headerText = headerOnLight ? hexToRgb('#1A1F2B') : rgb(1, 1, 1)
  const headerSubtleText = headerOnLight
    ? hexToRgb('#41506B')
    : hexToRgb('#E2E8EF')
  const textDark = hexToRgb('#1A1F2B')
  const textBody = hexToRgb('#2D3648')
  const textMuted = hexToRgb('#708095')
  const textFaint = hexToRgb('#A0AEBF')
  const seatBoxBorder = hexToRgb('#E2E8F0')
  const shadow = hexToRgb('#A0AEC0')

  const { regular, bold } = fonts

  if (showPageChrome) {
    page.drawText('EVENT TICKET  •  PRINTABLE  •  KEEP THIS FOR ENTRY', {
      x: 156.4,
      y: PAGE_HEIGHT - 30,
      size: 11,
      font: bold,
      color: hexToRgb('#495467'),
    })
  }

  // Soft drop shadow
  drawRoundedRect(page, {
    x: TICKET_X + 3,
    y: ticketY - 3,
    width: TICKET_W,
    height: TICKET_H,
    radius: CORNER_R,
    color: shadow,
  })

  // Ticket body
  drawRoundedRect(page, {
    x: TICKET_X,
    y: ticketY,
    width: TICKET_W,
    height: TICKET_H,
    radius: CORNER_R,
    color: bodyBg,
    borderColor: primary,
    borderWidth: 1.5,
  })

  // Header band + left stripe (drawn as solid rects clipped visually by border)
  const headerBottom = ticketY + TICKET_H - HEADER_H
  page.drawRectangle({
    x: TICKET_X,
    y: headerBottom,
    width: TICKET_W,
    height: HEADER_H,
    color: primary,
  })
  page.drawRectangle({
    x: TICKET_X,
    y: ticketY,
    width: STRIPE_W,
    height: TICKET_H,
    color: primary,
  })

  // Re-draw rounded border on top so corners stay clean
  drawRoundedRect(page, {
    x: TICKET_X,
    y: ticketY,
    width: TICKET_W,
    height: TICKET_H,
    radius: CORNER_R,
    borderColor: primary,
    borderWidth: 1.5,
  })

  const contentLeft = TICKET_X + 25
  const headerTop = ticketY + TICKET_H

  // Category badge
  const badgeLabel = theme.badgeLabel
  const badgePadX = 12
  const badgeTextWidth = bold.widthOfTextAtSize(badgeLabel, 9)
  const badgeW = Math.max(111, badgeTextWidth + badgePadX * 2)
  const badgeH = 23
  const badgeX = contentLeft
  const badgeY = headerTop - 42
  drawRoundedRect(page, {
    x: badgeX,
    y: badgeY,
    width: badgeW,
    height: badgeH,
    radius: 11,
    color: badgeColor,
  })
  page.drawText(badgeLabel, {
    x: badgeX + (badgeW - badgeTextWidth) / 2,
    y: badgeY + 7,
    size: 9,
    font: bold,
    color: rgb(1, 1, 1),
  })

  // Subtitle next to badge
  const subtitle = resolveSubtitle(event)
  if (subtitle) {
    const subtitleX = badgeX + badgeW + 12
    const subtitleMax = TICKET_X + TICKET_W - 110 - subtitleX
    page.drawText(fitText(subtitle, regular, 9, subtitleMax), {
      x: subtitleX,
      y: badgeY + 7,
      size: 9,
      font: regular,
      color: headerSubtleText,
    })
  }

  // Event title
  const eventName = fitText(event?.name, bold, 20, TICKET_W - 140) || 'Event'
  page.drawText(eventName, {
    x: contentLeft,
    y: headerTop - 58,
    size: 20,
    font: bold,
    color: headerText,
  })

  // Organization logo (header right)
  const logoUrl = resolveTicketLogoUrl(organization, event)
  const logoImage = await embedLogo(pdfDoc, logoUrl, logoCache)
  if (logoImage) {
    const maxLogoW = 70
    const maxLogoH = 40
    const scale = Math.min(
      maxLogoW / logoImage.width,
      maxLogoH / logoImage.height
    )
    const logoW = logoImage.width * scale
    const logoH = logoImage.height * scale
    page.drawImage(logoImage, {
      x: TICKET_X + TICKET_W - logoW - 18,
      y: headerTop - 52,
      width: logoW,
      height: logoH,
    })
  }

  // Body labels / values
  const labelColor = accentOn(primary)
  const drawField = (label, value, x, y) => {
    page.drawText(label, {
      x,
      y: y + 16,
      size: 8,
      font: bold,
      color: labelColor,
    })
    page.drawText(fitText(value, regular, 11, 230) || '—', {
      x,
      y,
      size: 11,
      font: regular,
      color: textBody,
    })
  }

  const venueName =
    event?.venue?.name?.trim() === 'Niagara IceDogs Hockey Club'
      ? 'Meridian Centre'
      : event?.venue?.name || ''
  const venueCity =
    event?.venue?.address?.city || event?.venue?.address?.[0]?.city
  const venue =
    venueCity && venueName && !venueName.includes(venueCity)
      ? `${venueName}, ${venueCity}`
      : venueName

  const offerInfo = resolvePrintedOfferInfo(ticket)
  drawField('DATE', dateLabel, contentLeft, headerBottom - 40)
  drawField(
    'TIME',
    offerInfo?.window || timeLabel,
    contentLeft,
    headerBottom - 80
  )
  drawField('VENUE', venue, contentLeft, headerBottom - 120)
  const organizationName = String(purchasedByOrganizationName || '').trim()
  if (organizationName) {
    drawField('ORGANIZATION', organizationName, contentLeft, headerBottom - 158)
  }

  // Ticket holder
  const holderX = TICKET_X + 270
  page.drawText('TICKET HOLDER', {
    x: holderX,
    y: headerBottom - 24,
    size: 8,
    font: bold,
    color: labelColor,
  })
  const holderName = fitText(ticketHolder, regular, 12, 140) || 'Guest'
  page.drawText(holderName, {
    x: holderX,
    y: headerBottom - 40,
    size: 12,
    font: regular,
    color: textBody,
  })
  page.drawLine({
    start: { x: holderX, y: headerBottom - 48 },
    end: { x: holderX + 140, y: headerBottom - 48 },
    thickness: 0.75,
    color: hexToRgb('#CBCFD8'),
  })

  // Offer name first so session tickets (Day Pass / Prelims / Finals) differ.
  const resolvedPackageName = resolvePackageName(packageName, ticket, event)
  if (offerInfo) {
    drawField('OFFER', offerInfo.label, holderX, headerBottom - 80)
  } else if (resolvedPackageName) {
    drawField('PACKAGE', resolvedPackageName, holderX, headerBottom - 80)
  }
  if (
    isNmStateAthleticsTicket(ticket, event, organization) &&
    ticket?.id != null
  ) {
    drawField(
      'TICKET ID',
      String(ticket.id),
      holderX,
      headerBottom - (offerInfo || resolvedPackageName ? 120 : 80)
    )
  }

  // Seat box
  const seatBoxX = contentLeft
  const seatBoxY = ticketY + 28
  const seatBoxW = 223
  const seatBoxH = 52
  drawRoundedRect(page, {
    x: seatBoxX,
    y: seatBoxY,
    width: seatBoxW,
    height: seatBoxH,
    radius: 8,
    color: rgb(1, 1, 1),
    borderColor: seatBoxBorder,
    borderWidth: 1,
  })

  const isGA = Boolean(ticket?.generalAdmission)
  const sectionValue = isGA
    ? ticket?.sectionNumber || 'General Admission'
    : String(ticket?.sectionNumber ?? '—')
  const rowValue = isGA ? 'GA' : String(ticket?.rowNumber ?? '—')
  const seatValue = isGA ? 'GA' : String(ticket?.seatNumber ?? '—')

  const seatCols = [
    { label: 'SECTION', value: sectionValue, x: seatBoxX + 22 },
    { label: 'ROW', value: rowValue, x: seatBoxX + 100 },
    { label: 'SEAT', value: seatValue, x: seatBoxX + 170 },
  ]
  seatCols.forEach((col) => {
    const labelW = bold.widthOfTextAtSize(col.label, 7)
    page.drawText(col.label, {
      x: col.x,
      y: seatBoxY + 36,
      size: 7,
      font: bold,
      color: labelColor,
    })
    const valueSize =
      String(col.value).length > 8 ? 11 : String(col.value).length > 4 ? 14 : 16
    const value = fitText(col.value, bold, valueSize, 60)
    const valueW = bold.widthOfTextAtSize(value, valueSize)
    page.drawText(value, {
      x: col.x + Math.max(0, (labelW - valueW) / 2),
      y: seatBoxY + 14,
      size: valueSize,
      font: bold,
      color: textDark,
    })
  })

  // QR code
  const qrSize = 118
  const qrX = TICKET_X + TICKET_W - qrSize - 24
  const qrY = ticketY + 40
  drawRoundedRect(page, {
    x: qrX - 6,
    y: qrY - 6,
    width: qrSize + 12,
    height: qrSize + 12,
    radius: 8,
    color: rgb(1, 1, 1),
    borderColor: seatBoxBorder,
    borderWidth: 1,
  })
  if (qrImage) {
    page.drawImage(qrImage, {
      x: qrX,
      y: qrY,
      width: qrSize,
      height: qrSize,
    })
  } else {
    page.drawText('QR Missing', {
      x: qrX + 20,
      y: qrY + qrSize / 2,
      size: 10,
      font: bold,
      color: textMuted,
    })
  }
  const scanLabel = 'SCAN AT ENTRANCE'
  const scanW = regular.widthOfTextAtSize(scanLabel, 7)
  page.drawText(scanLabel, {
    x: qrX + (qrSize - scanW) / 2,
    y: qrY - 16,
    size: 7,
    font: regular,
    color: textMuted,
  })

  // Footer policy line
  page.drawText(
    'Valid ID required  •  Non-transferable  •  Subject to venue policies',
    {
      x: contentLeft,
      y: ticketY + 10,
      size: 7,
      font: regular,
      color: textFaint,
    }
  )
}

/**
 * Pack branded tickets two-per LETTER page.
 * Each entry: { ticket, event, organization, purchasedByOrganizationName, qrImage, ticketHolder, dateLabel, timeLabel }
 */
export async function addBrandedTicketsToDocument(
  pdfDoc,
  ticketEntries,
  { fonts, logoCache = {} } = {}
) {
  const resolvedFonts = fonts || (await createTicketPdfFonts(pdfDoc))
  const slots = [TOP_TICKET_Y, BOTTOM_TICKET_Y]
  let page = null
  let slotIndex = 0

  for (const entry of ticketEntries) {
    if (!page || slotIndex >= TICKETS_PER_PAGE) {
      page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT])
      slotIndex = 0
    }

    await drawBrandedTicket(page, pdfDoc, {
      ...entry,
      fonts: resolvedFonts,
      logoCache,
      ticketY: slots[slotIndex],
      showPageChrome: slotIndex === 0,
    })
    slotIndex += 1
  }
}

/**
 * Draw one branded category ticket onto a new LETTER page (single-ticket helper).
 */
export async function addBrandedTicketPage(pdfDoc, options) {
  await addBrandedTicketsToDocument(pdfDoc, [options], {
    fonts: options.fonts,
    logoCache: options.logoCache,
  })
}

export function formatTicketHolderName({
  firstName,
  lastName,
  email,
  name,
} = {}) {
  const full = [firstName, lastName]
    .map((part) => (part ? String(part).trim() : ''))
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
  if (full) return full
  if (name) return String(name).trim()
  if (email) return String(email).trim()
  return 'Guest'
}
